'use client';

import { ConvexBetterAuthProvider } from '@convex-dev/better-auth/react';
import { ConvexQueryClient } from '@convex-dev/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConvexReactClient, useConvexAuth, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { ConfirmedConvexIdentityContext } from './protected-workspace-boundary';
import { ConvexQueryCacheProvider } from 'convex-helpers/react/cache';
import { ReactNode, useEffect, useState } from 'react';
import { authClient } from '../lib/better-auth-client-actions';
import { createConvexQueryAuthLifecycle } from '../lib/convex-query-auth-lifecycle';

const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL!, {
  verbose: process.env.NEXT_PUBLIC_CONVEX_VERBOSE === 'true',
  // No "Reload site?" beforeunload dialog for in-flight mutations. Surfaces
  // like Work Chats fire constant background mutations (markRead debounce,
  // presence heartbeats), so the warning triggered on ~every reload; losing
  // one of those on unload is harmless, and real drafts are persisted
  // separately.
  unsavedChangesWarning: false,
});
const convexQueryClient = new ConvexQueryClient(convex);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
    },
  },
});
convexQueryClient.connect(queryClient);
const updateConvexQueryIdentity = createConvexQueryAuthLifecycle(queryClient);

function ConvexQueryAuthLifecycle({ children }: { children: ReactNode }) {
  const { data: session, isPending } = authClient.useSession();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const userId = session?.user.id ?? null;
  const viewer = useQuery(
    api.users.queries.viewer,
    !isPending && !isLoading && isAuthenticated && userId ? {} : 'skip'
  );
  const confirmedIdentity =
    !isPending && !isLoading && isAuthenticated && viewer?._id === userId
      ? userId
      : null;

  const [admission, setAdmission] = useState({
    identity: confirmedIdentity,
    ready: false,
  });
  if (admission.identity !== confirmedIdentity) {
    setAdmission({ identity: confirmedIdentity, ready: false });
  }

  useEffect(() => {
    let current = true;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const attempt = () => {
      attempts += 1;
      void updateConvexQueryIdentity(confirmedIdentity)
        .then(() => {
          if (current)
            setAdmission({ identity: confirmedIdentity, ready: true });
        })
        .catch(() => {
          if (!current) return;
          setAdmission({ identity: confirmedIdentity, ready: false });
          if (attempts < 3)
            retryTimer = setTimeout(attempt, 250 * 2 ** (attempts - 1));
        });
    };
    attempt();
    return () => {
      current = false;
      clearTimeout(retryTimer);
    };
  }, [confirmedIdentity]);

  return (
    <ConfirmedConvexIdentityContext.Provider
      value={
        admission.identity === confirmedIdentity && admission.ready
          ? confirmedIdentity
          : null
      }
    >
      <ConvexQueryCacheProvider key={userId ?? 'anonymous'}>
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      </ConvexQueryCacheProvider>
    </ConfirmedConvexIdentityContext.Provider>
  );
}

export function ConvexClientProvider({
  children,
  initialToken,
}: {
  children: ReactNode;
  initialToken?: string | null;
}) {
  return (
    <ConvexBetterAuthProvider
      client={convex}
      authClient={authClient}
      initialToken={initialToken ?? undefined}
    >
      <ConvexQueryAuthLifecycle>{children}</ConvexQueryAuthLifecycle>
    </ConvexBetterAuthProvider>
  );
}
